import { useEffect, useRef, useState } from 'react';
import { hasConsent } from '@/lib/consent';
import type { SocialPost } from '@/data/social';

/**
 * The real post embeds behind the home page's social columns.
 *
 * Each platform renders its own card in the browser, so nothing is copied here
 * and nothing needs an API key:
 *
 *   X          a blockquote that `platform.twitter.com/widgets.js` replaces
 *   Instagram  a blockquote that `instagram.com/embed.js` replaces
 *   LinkedIn   an iframe it serves itself
 *
 * All three set third-party cookies, so none of them loads until the visitor
 * has accepted marketing cookies. Until then each post shows a card with a
 * link to the post — the section stays useful rather than going blank.
 */

const SCRIPTS: Partial<Record<SocialPost['network'], string>> = {
    x: 'https://platform.twitter.com/widgets.js',
    instagram: 'https://www.instagram.com/embed.js',
};

declare global {
    interface Window {
        twttr?: { widgets?: { load?: (el?: HTMLElement) => void } };
        instgrm?: { Embeds?: { process?: () => void } };
    }
}

/** Loads a platform's script once per page, then asks it to sweep for new blockquotes. */
function useEmbedScript(network: SocialPost['network'], enabled: boolean, container: HTMLElement | null): void {
    useEffect(() => {
        const src = SCRIPTS[network];
        if (!enabled || !src || !container) return;

        const sweep = () => {
            if (network === 'x') window.twttr?.widgets?.load?.(container);
            else window.instgrm?.Embeds?.process?.();
        };

        const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
        if (existing) {
            sweep();
            return;
        }
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = sweep;
        document.body.appendChild(script);
    }, [network, enabled, container]);
}

/** Shown before consent, and if a platform's script never arrives. */
function PostFallback({ post }: { post: SocialPost }) {
    const where = post.network === 'x' ? 'X' : post.network === 'instagram' ? 'Instagram' : 'LinkedIn';
    return (
        <div className="rounded-[12px] border border-[#e4e4e4] bg-white p-4">
            {post.caption && <p className="text-[14px] leading-[22px] text-[#333]">{post.caption}</p>}
            <a
                href={post.url || post.embedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-block text-[13px] font-bold text-[#f60] underline ${post.caption ? 'mt-3' : ''}`}
            >
                Open this post on {where}
            </a>
        </div>
    );
}

export function SocialFeed({ network, posts }: { network: SocialPost['network']; posts: SocialPost[] }) {
    const [allowed, setAllowed] = useState(() => hasConsent('marketing'));
    const [host, setHost] = useState<HTMLDivElement | null>(null);
    const seen = useRef(0);

    // The banner can be answered after this has mounted.
    useEffect(() => {
        const onChange = () => setAllowed(hasConsent('marketing'));
        window.addEventListener('cg:consent-changed', onChange);
        return () => window.removeEventListener('cg:consent-changed', onChange);
    }, []);

    useEmbedScript(network, allowed, host);

    // A column whose posts arrive late still needs the platform to sweep again.
    useEffect(() => {
        if (posts.length !== seen.current) seen.current = posts.length;
    }, [posts.length]);

    if (!posts.length) return null;

    if (!allowed) {
        return (
            <div className="flex flex-col gap-4">
                <p className="rounded-[12px] bg-[#efefef] p-4 text-[13px] leading-[20px] text-[#555]">
                    These posts come from {network === 'x' ? 'X' : network === 'instagram' ? 'Instagram' : 'LinkedIn'},
                    which sets its own cookies. Accept marketing cookies to show them here, or open them on the
                    platform.
                </p>
                {posts.map((post) => (
                    <PostFallback key={post.embedUrl} post={post} />
                ))}
            </div>
        );
    }

    return (
        <div ref={setHost} className="flex flex-col gap-4">
            {posts.map((post) =>
                network === 'linkedin' ? (
                    <iframe
                        key={post.embedUrl}
                        src={post.embedUrl}
                        title={post.caption || 'LinkedIn post'}
                        height={640}
                        className="w-full border-0"
                        allowFullScreen
                    />
                ) : network === 'x' ? (
                    <blockquote key={post.embedUrl} className="twitter-tweet" data-dnt="true">
                        <a href={post.embedUrl}>{post.caption}</a>
                    </blockquote>
                ) : (
                    <blockquote
                        key={post.embedUrl}
                        className="instagram-media"
                        data-instgrm-permalink={post.embedUrl}
                        data-instgrm-version="14"
                        style={{ background: '#fff', border: 0, margin: 0, padding: 0, width: '100%' }}
                    />
                ),
            )}
        </div>
    );
}
